/** 離陸の条件（docs/03_simulation.md 7 章） */
import { LAUNCH } from '../../config/balance';
import { angleDiff } from '../../core/math';
import type { NodeData } from '../../core/world';
import { statLerp } from '../athlete/stats';
import type { Stats } from '../types';
import { windForecastAt, type Forecast } from '../weather/forecast';
import type { WeatherModel } from '../weather/model';

export interface LaunchCheck {
  /** 離陸できるか。できないのは強風のときだけ */
  ok: boolean;
  /** 離陸する向き（風上に最も近い、余裕のある向き） */
  headingRad: number;
  /** strong＝強風で離陸できない、tailwind＝追い風・横風で失敗しやすい（離陸はできる） */
  reason: 'strong' | 'tailwind' | null;
  /** 追い風・横風の離陸の失敗確率（向かい風なら 0） */
  tailwindFailP: number;
  windKmh: number;
  /** 最大風速に対する比率（離陸失敗の判定用） */
  nearLimit: number;
}

export const maxLaunchWindKmh = (s: Stats): number =>
  statLerp(s.glider, LAUNCH.maxWindKmh[0], LAUNCH.maxWindKmh[1]);

export function checkLaunch(
  weather: WeatherModel,
  node: NodeData,
  timeOfDayS: number,
  stats: Stats,
): LaunchCheck {
  const w = weather.windAt(node.pos.x, node.pos.y + 10, node.pos.z, timeOfDayS, { x: 0, z: 0 });
  const kmh = Math.hypot(w.x, w.z) * 3.6;
  const max = maxLaunchWindKmh(stats);
  const nearLimit = kmh / max;
  const facings = node.launch?.facings.length ? node.launch.facings : [node.launch?.facingRad ?? 0];
  const from = Math.atan2(-w.x, w.z);
  // 風上に最も近い向き（無風に近ければ代表の向き）
  let headingRad = node.launch?.facingRad ?? 0;
  if (kmh >= LAUNCH.calmKmh) {
    headingRad = facings.reduce(
      (best, d) => (angleDiff(d, from) < angleDiff(best, from) ? d : best),
      facings[0] as number,
    );
  }
  if (kmh > max)
    return { ok: false, headingRad, reason: 'strong', windKmh: kmh, nearLimit, tailwindFailP: 0 };
  if (kmh >= LAUNCH.calmKmh && angleDiff(from, headingRad) > (LAUNCH.maxHeadAngleDeg * Math.PI) / 180) {
    const t = LAUNCH.tailwind;
    const p = Math.min(t.maxP, t.base + t.perKmh * (kmh - LAUNCH.calmKmh));
    return { ok: true, headingRad, reason: 'tailwind', windKmh: kmh, nearLimit, tailwindFailP: p };
  }
  return { ok: true, headingRad, reason: null, windKmh: kmh, nearLimit, tailwindFailP: 0 };
}

/** 予報の風から見た離陸の見通し（予約の表示用）。good＝向かい風か弱風、tailwind＝失敗しやすい、strong＝飛べない恐れ */
export function launchOutlook(
  f: Forecast,
  node: NodeData,
  hour: number,
  stats: Stats,
): { kind: 'good' | 'tailwind' | 'strong'; kmh: number } {
  const level = node.launch?.level ?? 'low';
  const wf = windForecastAt(f, hour, level);
  if (wf.kmh > maxLaunchWindKmh(stats)) return { kind: 'strong', kmh: wf.kmh };
  const from = (wf.fromDeg * Math.PI) / 180;
  const facings = node.launch?.facings.length ? node.launch.facings : [node.launch?.facingRad ?? 0];
  const maxHead = (LAUNCH.maxHeadAngleDeg * Math.PI) / 180;
  if (wf.kmh >= LAUNCH.calmKmh && !facings.some((d) => angleDiff(from, d) <= maxHead))
    return { kind: 'tailwind', kmh: wf.kmh };
  return { kind: 'good', kmh: wf.kmh };
}
